import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import {
	CaseCard,
	Typography,
	EmptyFiltersScreen,
	LoaderScreen,
	Icon,
	FunnelEmptyScreen,
	LabelShowingResults,
	Button,
} from '@/components/common';

import { ContainerBase } from '@/components/landing/containers';
import { useClaimIcons, type BadgeTypes } from '@/hooks/useClaimIcons';
import { useInternalCaseFilter } from '@/hooks';
import { MagnifyingGlass } from '@/public/icons/tsx';

import { Combobox } from '@/components/ui/combobox';

import {
	categoriesService,
	casesService,
	GET_CASES_STATUS,
	GET_CATEGORIES,
	GET_CASES,
	GET_CASES_STATES,
} from '@/api';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { GC_TIME, STALE_TIME } from '@/const/common';

interface Options {
	value: string;
	label: JSX.Element | string;
	icon?: JSX.Element;
}

export const CasesGridMobile: React.FC = () => {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const { replace } = useRouter();

	const params = new URLSearchParams(searchParams);

	const searchPage = params.get('page') ? Number(params.get('page')) : 1;

	const [searchValue, setSearchValue] = useState('');

	const [category, setCategory] = useState<string | undefined>('');
	const [openCategories, setOpenCategories] = useState(false);
	const [optionsCategories, setOptionsCategories] = useState<Options[]>([]);

	const [status, setStatus] = useState<string | undefined>('');
	const [openStatus, setOpenStatus] = useState(false);
	const [optionsStatus, setOptionsStatus] = useState<Options[]>([]);

	const [state, setState] = useState<string | undefined>('');
	const [openState, setOpenState] = useState(false);
	const [optionsState, setOptionsState] = useState<Options[]>([]);

	const [showFilters, setShowFilters] = useState(false);

	const totalRecords = useRef(null);

	const { getIcon } = useClaimIcons();

	const { data: categoriesRes } = useQuery({
		queryKey: [GET_CATEGORIES],
		queryFn: () => categoriesService.getAll(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME, // 7 días en cache
	});

	const { data: statusesRes } = useQuery({
		queryKey: [GET_CASES_STATUS],
		queryFn: () => casesService.getCasesStatus(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
	});

	const { data: statesRes } = useQuery({
		queryKey: [GET_CASES_STATES],
		queryFn: () => casesService.getStates(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
	});

	const searchStatus = status;

	const {
		data: casesRes,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		refetch,
	} = useInfiniteQuery({
		queryKey: [GET_CASES, category, status, state, searchValue],
		queryFn: ({ pageParam = 1 }) =>
			casesService.getAll({
				page: pageParam,
				limit: 8,
				categoryId: category ? Number(category) : undefined,
				statusId: status ? Number(status) : undefined,
				eligibleStateId: state ? Number(state) : undefined,
				search: encodeURIComponent(searchValue),
			}),
		getNextPageParam: (lastPage, allPages) => {
			const totalPages = Math.ceil(lastPage.data.totalItems / 8);
			const nextPage = allPages.length + 1;
			return nextPage <= totalPages ? nextPage : undefined;
		},
		initialPageParam: 1,
	});

	const categories = categoriesRes?.data;
	const statuses = statusesRes?.data;
	const states = statesRes?.data;

	useEffect(() => {
		if (category) {
			const labelCategory = categories?.find(
				(cat) => cat.id === Number(category)
			);
			if (labelCategory) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [category, categories]);

	useEffect(() => {
		if (status) {
			const labelStatus = statuses?.find((stat) => stat.id === Number(status));
			if (labelStatus) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [statuses, status]);

	useEffect(() => {
		if (state) {
			const labelState = states?.find((stat) => stat.id === Number(state));

			if (labelState) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [state, states]);

	const handleSearch = useDebouncedCallback(() => {
		fetchAndReplace();
	}, 300);

	const fetchAndReplace = () => {
		setTimeout(() => {
			if (category || status || state) {
				params.set('page', '1');
			} else {
				params.set('page', String(searchPage));
			}
			replace(`${pathname}?${params.toString()}`);
			refetch();
		}, 50);
	};

	const { filterInternalCases } = useInternalCaseFilter();
	const casesData = filterInternalCases(
		casesRes?.pages.flatMap((page) => page.data.items) ?? []
	);
	const totalItems = casesRes?.pages[0]?.data.totalItems ?? 0;

	useEffect(() => {
		if (totalRecords.current === null && totalItems) {
			(totalRecords.current as number | null) = totalItems;
		}
	}, [totalItems]);

	// -------------- Fill Categories, Status and States ------------//
	useEffect(() => {
		const parsedStates: Options[] = [];
		states?.map((state) => {
			return parsedStates.push({
				value: String(state.id),
				label: state.name,
			});
		});
		setOptionsState([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedStates,
		]);
	}, [states]);

	useEffect(() => {
		const parsedCategories: Options[] = [];
		categories?.map((category) => {
			return parsedCategories.push({
				value: String(category.id),
				label: category.name,
				icon: getIcon(category.name as BadgeTypes),
			});
		});
		setOptionsCategories([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedCategories,
		]);
	}, [categories]);

	useEffect(() => {
		const parsedStatuses: Options[] = [];
		statuses?.map((status) => {
			return parsedStatuses.push({
				value: String(status.id),
				label: status.name,
			});
		});
		setOptionsStatus([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedStatuses,
		]);
	}, [statuses]);

	useEffect(() => {
		refetch();
	}, [category, status, state, searchValue]);

	// ------------------ Handles to add or remove Clear Button -----------//
	const handleCategory = (value: string | undefined): void => {
		if (value === 'clear') {
			setCategory('');
		} else {
			setCategory(value);
		}
	};

	const handleStatus = (value: string | undefined): void => {
		if (value === 'clear') {
			setStatus('');
		} else {
			setStatus(value);
		}
	};

	const handleState = (value: string | undefined): void => {
		if (value === 'clear') {
			setState('');
		} else {
			setState(value);
		}
	};

	return (
		<ContainerBase className="block sm:hidden relative z-10">
			<div className="flex flex-col md:flex-row items-baseline xl:items-center justify-between pt-10 pb-10 ">
				<div className="flex flex-wrap gap-6 w-full ">
					<div className="w-full flex items-center gap-4">
						<div className="flex items-center bg-[#F4F4F9] rounded-[100px] h-8 py-2 px-4 gap-2 w-full md:w-[283px]">
							<MagnifyingGlass className="!size-5" />
							<input
								type="text"
								value={searchValue}
								className="outline-none bg-transparent flex-1"
								placeholder="Search"
								onChange={(e) => {
									handleSearch();
									setSearchValue(e.target.value);
								}}
							/>
						</div>
						<Button
							decoration="primary"
							className={clsx(
								'rounded-lg !w-[30px] h-8 flex items-center justify-center !p-0 shrink-0',
								{
									'!bg-[#F4F4F9] !border-0': !showFilters,
								}
							)}
							onClick={() => setShowFilters((prev) => !prev)}
						>
							<Icon
								src={Icons.funnel}
								className={clsx('!w-[14px] !text-[#6E6A73]', {
									'!text-white': showFilters,
								})}
							/>
						</Button>
					</div>
					<div
						className={clsx(
							'opacity-0 max-h-0 flex flex-col md:flex-row items-start md:items-center w-full md:w-auto gap-2 z-0 transition-all duration-300 ease-out',
							{
								'opacity-100 max-h-40': showFilters,
							}
						)}
					>
						<Combobox
							value={category}
							options={optionsCategories}
							open={openCategories}
							placeholder="Category"
							classNamePopoverContent="!w-[333px] ml-5"
							classNameTrigger="!h-[33px]"
							setOpen={(value) => setOpenCategories(value)}
							setValue={(value) => handleCategory(value)}
						/>
						<Combobox
							value={status}
							open={openStatus}
							options={optionsStatus}
							placeholder="Cases Status"
							classNameTrigger="!h-[33px]"
							classNamePopoverContent="!w-[333px] ml-5"
							setOpen={(value) => setOpenStatus(value)}
							setValue={(value) => handleStatus(value)}
						/>
						<Combobox
							isSearch
							open={openState}
							placeholder="Eligible State"
							options={optionsState}
							value={state}
							setOpen={(value) => setOpenState(value)}
							setValue={(value) => handleState(value)}
							classNameTrigger="!h-[33px]"
							classNamePopoverContent="!w-[333px] ml-5"
							classNameCommandList="!max-h-[336px] overflow-y-scroll"
						/>
					</div>
				</div>
				<div
					className={clsx('w-full xl:w-auto pt-6 md:pt-0', {
						'!pt-0': !showFilters,
					})}
				>
					<LabelShowingResults
						totalItems={totalItems}
						total={casesData.length}
						labelTotalItems="cases"
						typographyType="regular-16"
						isSearched={
							!!searchStatus || !!searchValue || !!category || !!state
						}
					/>
				</div>
			</div>
			<div>
				{isLoading && <LoaderScreen className="my-10" />}
				{!isLoading && casesData.length === 0 && !searchValue && (
					<FunnelEmptyScreen
						message="There are currently no cases matching the selected filters"
						className="pt-8"
					/>
				)}

				{!isLoading && casesData.length === 0 && searchValue && (
					<EmptyFiltersScreen>
						<>
							<Typography type="regular-18">
								We couldn&apos;t find a case for &quot;{searchValue}&quot;.
							</Typography>
							<br />
							<Typography type="regular-18">
								Please try again with a different word.
							</Typography>
						</>
					</EmptyFiltersScreen>
				)}
				{!isLoading && casesData.length > 0 && (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-6 mb-6">
						{casesData.map((item, index) => (
							<CaseCard
								key={index}
								{...item}
								classNameButton="!h-[48px] !min-w-[133px]"
							/>
						))}
					</div>
				)}
				<Button
					type="button"
					decoration="line-primary"
					size="fit"
					className={clsx('!h-12 !w-full md:!w-fit mx-auto !mt-2', {
						hidden: !hasNextPage,
					})}
					onClick={() => fetchNextPage()}
					disabled={isFetchingNextPage}
				>
					See More
				</Button>
			</div>
		</ContainerBase>
	);
};

interface ClearButtonProps {
	className?: string;
}

export const ClearButton: React.FC<ClearButtonProps> = ({ className }) => {
	return (
		<Typography type="regular-16" className={className}>
			Clear All
		</Typography>
	);
};
